import { useEffect, useRef, useState } from "react";
import { Gear, User, LockKey, SignOut, PencilSimple, ImageSquare, Check } from "@phosphor-icons/react";
import api, { API } from "../lib/api";
import { useAuth } from "../lib/auth";

/**
 * SettingsPage — profile edit, avatar, password, account.
 * Uses the existing /api/users/me + /api/auth/change-password + /api/uploads/image endpoints.
 */
export default function SettingsPage() {
  const { user, logout, fetchMe } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [username, setUsername] = useState(user?.username || "");
  const [bio, setBio] = useState(user?.bio || "");
  const [avatar, setAvatar] = useState(user?.avatar_image || null);
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileMsg, setProfileMsg] = useState("");

  const [oldPass, setOldPass] = useState("");
  const [newPass, setNewPass] = useState("");
  const [pwMsg, setPwMsg] = useState("");
  const [savingPw, setSavingPw] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    setName(user?.name || "");
    setUsername(user?.username || "");
    setBio(user?.bio || "");
    setAvatar(user?.avatar_image || null);
  }, [user?.user_id]);

  const uploadAvatar = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    e.target.value = "";
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAvatar(data.file_id);
    } catch (ex) { alert(ex?.response?.data?.detail || "Upload failed"); }
  };

  const saveProfile = async (e) => {
    e?.preventDefault();
    setSavingProfile(true); setProfileMsg("");
    try {
      await api.put("/users/me", {
        name, username, bio: bio || null, avatar_image: avatar,
      });
      setProfileMsg("Saved ✓");
      fetchMe?.();
      setTimeout(() => setProfileMsg(""), 2000);
    } catch (ex) {
      setProfileMsg(ex?.response?.data?.detail || "Save failed");
    } finally { setSavingProfile(false); }
  };

  const changePassword = async (e) => {
    e?.preventDefault();
    setPwMsg(""); setSavingPw(true);
    try {
      await api.post("/auth/change-password", { old_password: oldPass, new_password: newPass });
      setPwMsg("Password updated ✓");
      setOldPass(""); setNewPass("");
      setTimeout(() => setPwMsg(""), 2500);
    } catch (ex) {
      setPwMsg(ex?.response?.data?.detail || "Change failed");
    } finally { setSavingPw(false); }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="settings-page">
      <header>
        <div className="text-[11px] uppercase tracking-[0.32em] text-[#F2DC8C]/60">
          Command Center · Settings
        </div>
        <h1 className="mt-1 font-display text-[34px] leading-tight"
            style={{
              background: "linear-gradient(180deg, #FFF3AE 0%, #FFD700 45%, #8a6a00 100%)",
              WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
              letterSpacing: "0.02em",
            }}>
          Your Account
        </h1>
      </header>

      {/* Profile */}
      <form onSubmit={saveProfile} className="rounded-2xl p-6"
            data-testid="settings-profile-card"
            style={{
              background: "linear-gradient(180deg, rgba(26,18,6,0.85) 0%, rgba(5,3,0,0.95) 100%)",
              border: "1px solid rgba(212,175,55,0.4)",
            }}>
        <div className="flex items-center gap-2 mb-4">
          <User size={14} weight="fill" className="text-[#FFD700]"/>
          <div className="text-[11px] uppercase tracking-[0.28em] text-[#F2DC8C]">Profile</div>
          {profileMsg && (
            <span className="ml-auto text-[11px] text-[#FFD700]" data-testid="settings-profile-msg">{profileMsg}</span>
          )}
        </div>

        <div className="flex items-center gap-4 mb-5">
          <div className="w-20 h-20 rounded-full overflow-hidden flex items-center justify-center shrink-0"
               style={{ border: "2px solid #D4AF37", boxShadow: "0 6px 18px rgba(255,215,0,0.35)" }}>
            {avatar ? (
              <img src={`${API}/files/${avatar}`} alt="" className="w-full h-full object-cover"/>
            ) : (
              <span className="text-[24px] font-display text-[#FFD700]">
                {(name || username || "?").slice(0, 1).toUpperCase()}
              </span>
            )}
          </div>
          <div>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadAvatar} data-testid="settings-avatar-input"/>
            <button type="button" onClick={() => fileRef.current?.click()}
                    data-testid="settings-avatar-upload"
                    className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg font-display uppercase text-[10px] tracking-[0.22em]"
                    style={{ background: "linear-gradient(180deg, #FFF3AE, #D4AF37 60%, #6b4d00)", color: "#0a0703" }}>
              <ImageSquare size={11}/> Change Avatar
            </button>
            <div className="mt-1 text-[10px] uppercase tracking-widest text-[#F2DC8C]/55">JPG / PNG · Any size</div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
          <Labeled label="Display name">
            <input data-testid="settings-name" value={name}
                   onChange={(e) => setName(e.target.value.slice(0, 100))}
                   className={inputCls}/>
          </Labeled>
          <Labeled label="Username">
            <input data-testid="settings-username" value={username}
                   onChange={(e) => setUsername(e.target.value.trim().toLowerCase().slice(0, 30))}
                   className={inputCls}/>
          </Labeled>
        </div>
        <Labeled label="Bio">
          <textarea data-testid="settings-bio" value={bio} rows={3}
                    onChange={(e) => setBio(e.target.value.slice(0, 500))}
                    placeholder="A sentence or two about you."
                    className={inputCls + " resize-none"}/>
        </Labeled>
        <div className="mt-4">
          <button type="submit" disabled={savingProfile || !name.trim() || !username.trim()}
                  data-testid="settings-profile-save"
                  className="inline-flex items-center gap-2 h-10 px-5 rounded-xl font-display uppercase text-[11px] tracking-[0.22em] disabled:opacity-40"
                  style={{ background: "linear-gradient(180deg, #FFF3AE, #D4AF37 60%, #6b4d00)", color: "#0a0703",
                           boxShadow: "0 6px 20px rgba(255,215,0,0.35)" }}>
            <Check size={11} weight="bold"/> {savingProfile ? "Saving…" : "Save Profile"}
          </button>
        </div>
      </form>

      {/* Password */}
      <form onSubmit={changePassword} className="rounded-2xl p-6"
            data-testid="settings-password-card"
            style={{
              background: "linear-gradient(180deg, rgba(26,18,6,0.85) 0%, rgba(5,3,0,0.95) 100%)",
              border: "1px solid rgba(212,175,55,0.4)",
            }}>
        <div className="flex items-center gap-2 mb-4">
          <LockKey size={14} weight="fill" className="text-[#FFD700]"/>
          <div className="text-[11px] uppercase tracking-[0.28em] text-[#F2DC8C]">Password</div>
          {pwMsg && (
            <span className="ml-auto text-[11px]" data-testid="settings-password-msg"
                  style={{ color: pwMsg.includes("✓") ? "#FFD700" : "#FF5D73" }}>{pwMsg}</span>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Labeled label="Current password">
            <input data-testid="settings-old-pass" type="password" value={oldPass}
                   onChange={(e) => setOldPass(e.target.value)} className={inputCls}/>
          </Labeled>
          <Labeled label="New password">
            <input data-testid="settings-new-pass" type="password" value={newPass}
                   onChange={(e) => setNewPass(e.target.value)} className={inputCls}/>
          </Labeled>
        </div>
        <div className="mt-4">
          <button type="submit" disabled={savingPw || !oldPass || newPass.length < 8}
                  data-testid="settings-password-save"
                  className="inline-flex items-center gap-2 h-10 px-5 rounded-xl font-display uppercase text-[11px] tracking-[0.22em] disabled:opacity-40"
                  style={{ background: "linear-gradient(180deg, #FFF3AE, #D4AF37 60%, #6b4d00)", color: "#0a0703" }}>
            <LockKey size={11}/> {savingPw ? "Saving…" : "Update Password"}
          </button>
        </div>
      </form>

      {/* Danger zone */}
      <div className="rounded-2xl p-6 text-center"
           style={{ background: "rgba(0,0,0,0.5)", border: "1px solid rgba(255,93,115,0.35)" }}>
        <button onClick={logout} data-testid="settings-logout"
                className="inline-flex items-center gap-2 h-10 px-5 rounded-xl font-display uppercase text-[11px] tracking-[0.22em] text-[#FF5D73]"
                style={{ border: "1px solid rgba(255,93,115,0.55)" }}>
          <SignOut size={11}/> Log out of HigherVision
        </button>
      </div>
    </div>
  );
}


const inputCls =
  "w-full bg-transparent px-3 py-2 rounded-lg text-[13px] text-[#F2DC8C] placeholder:text-[#F2DC8C]/40 focus:outline-none";

function Labeled({ label, children }) {
  return (
    <label className="block">
      <div className="text-[9px] uppercase tracking-[0.22em] text-[#F2DC8C]/70 mb-1">{label}</div>
      <div style={{ border: "1px solid rgba(212,175,55,0.4)", background: "rgba(0,0,0,0.35)", borderRadius: 10 }}>
        {children}
      </div>
    </label>
  );
}
